'use client';

import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/Button';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useToastActions } from '../components/ui/Toast';
import { useWorkflowListStore, SavedWorkflow } from '../stores/workflowListStore';
import { workflowApi as _workflowApi } from '../services/api';
import { DataTable } from '../components/workflow/DataArea';
import { DiffResultView, type DiffResultViewRef } from '../components/workflow/DiffResultView';
import {
  Plus,
  MagnifyingGlass,
  FlowArrow,
  Clock,
  XCircle,
  PencilSimple,
  Trash,
  Play,
  DotsThree,
  FileXls,
  Table,
  Spinner,
  Calendar,
  AppWindow,
  DownloadSimple,
  WarningCircle,
  Lightning,
  ArrowsLeftRight,
} from '@phosphor-icons/react';

// ── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active:   'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    draft:    'bg-amber-50 text-amber-700 border-amber-200',
  };
  const labels: Record<string, string> = {
    active: '已启用', inactive: '已停用', draft: '草稿',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.inactive}`}>
      {labels[status] || status}
    </span>
  );
}

// ── Run Workflow Modal ────────────────────────────────────────────────────────
type RunState = 'idle' | 'uploading' | 'running' | 'done' | 'error';

// 输入节点类型
const INPUT_NODE_TYPES = ['load_excel', 'load_csv'];

interface InputNodeInfo {
  id: string;
  type: string;
  label: string;
  file: File | null;
}

interface ExecutionResultItem {
  nodeId: string;
  label: string;
  data: Record<string, unknown>[];
  columns: string[];
  rowCount: number;
  metadata?: Record<string, unknown>;
  isDiffResult?: boolean;
}

function RunWorkflowModal({
  isOpen,
  onClose,
  workflow,
}: {
  isOpen: boolean;
  onClose: () => void;
  workflow?: SavedWorkflow;
}) {
  const toast = useToastActions();
  const { incrementExecutionCount } = useWorkflowListStore();

  const [runState, setRunState] = useState<RunState>('idle');
  const [inputNodes, setInputNodes] = useState<InputNodeInfo[]>([]);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [executionResults, setExecutionResults] = useState<ExecutionResultItem[]>([]);
  const [activeResultTab, setActiveResultTab] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const diffViewRef = useRef<DiffResultViewRef | null>(null);

  // 从工作流中提取输入节点
  React.useEffect(() => {
    if (!workflow) {
      setInputNodes([]);
      return;
    }
    const nodes: InputNodeInfo[] = workflow.nodes
      .filter((n) => INPUT_NODE_TYPES.includes(n.type))
      .map((n) => ({
        id: n.id,
        type: n.type,
        label: n.data?.label || (n.type === 'load_csv' ? 'CSV 输入' : 'Excel 输入'),
        file: null,
      }));
    setInputNodes(nodes);
  }, [workflow]);

  const resetState = () => {
    setRunState('idle');
    setInputNodes((prev) => prev.map((n) => ({ ...n, file: null })));
    setExecutionResults([]);
    setActiveResultTab(0);
    setErrorMessage('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = (nodeId: string, file: File) => {
    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls') && !ext.endsWith('.csv')) {
      toast.error('仅支持 .xlsx、.xls、.csv 格式');
      return;
    }
    setInputNodes((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, file } : n))
    );
    setRunState('idle');
    setExecutionResults([]);
  };

  const handleDrop = (e: React.DragEvent, nodeId: string) => {
    e.preventDefault();
    setDraggingNodeId(null);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(nodeId, file);
  };

  const handleClearFile = (nodeId: string) => {
    setInputNodes((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, file: null } : n))
    );
  };

  const allFilesSelected = inputNodes.length > 0 && inputNodes.every((n) => n.file !== null);

  const handleExecute = async () => {
    if (!allFilesSelected || !workflow) return;

    setRunState('uploading');
    try {
      const files = inputNodes.map((n) => ({ nodeId: n.id, file: n.file! }));
      const result = await _workflowApi.runWorkflow(workflow.id, files, {
        name: workflow.name,
        description: workflow.description,
        nodes: workflow.nodes,
        edges: workflow.edges,
      });
      setRunState('running');
      await new Promise((r) => setTimeout(r, 400));

      // 与设计页面一致：构建 executionResults（分组 / 单结果 / 差异对比）
      if (result.is_grouped && result.grouped_results && result.grouped_results.length > 0) {
        const groupItems: ExecutionResultItem[] = result.grouped_results.map((g, idx) => ({
          nodeId: `__group_${idx}__`,
          label: `分组: ${g.group_key}`,
          data: g.rows,
          columns: g.columns,
          rowCount: g.rowCount,
        }));
        groupItems.push({
          nodeId: '__merged__',
          label: '合并结果',
          data: result.rows,
          columns: result.columns,
          rowCount: result.rowCount,
        });
        setExecutionResults(groupItems);
      } else {
        const isDiff = result.columns.includes('diff_type');
        setExecutionResults([{
          nodeId: '__result_0__',
          label: isDiff ? '差异对比结果' : '执行结果',
          data: result.rows,
          columns: result.columns,
          rowCount: result.rowCount,
          metadata: result.metadata,
          isDiffResult: isDiff,
        }]);
      }
      setActiveResultTab(0);
      setRunState('done');
      incrementExecutionCount(workflow.id);
      toast.success(`工作流执行完成，共 ${result.rowCount} 行结果`);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : '执行失败，请重试');
      setRunState('error');
    }
  };

  const handleDownloadResult = (
    index: number,
    exportOverride?: { columns: string[]; rows: string[][] }
  ) => {
    const result = executionResults[index];
    if (!result) return;
    let aoa: unknown[][];
    if (exportOverride) {
      aoa = [exportOverride.columns, ...exportOverride.rows];
    } else {
      aoa = [
        result.columns,
        ...result.data.map((row) => result.columns.map((c) => (row[c] ?? '') as string)),
      ];
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '结果');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflow?.name || 'result'}_${Date.now()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen || !workflow) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <AppWindow className="w-5 h-5 text-emerald-600" weight="fill" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">应用工作流</h3>
                <p className="text-xs text-zinc-500 max-w-[24ch] truncate">{workflow.name}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Input Files Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  第一步：上传输入文件
                </p>
                <span className="text-[10px] text-zinc-400">
                  共 {inputNodes.length} 个输入节点
                </span>
              </div>

              {inputNodes.length === 0 ? (
                <div className="p-6 bg-zinc-50 rounded-2xl border border-zinc-200 text-center">
                  <WarningCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" weight="fill" />
                  <p className="text-sm text-zinc-600">该工作流没有输入节点</p>
                  <p className="text-xs text-zinc-400 mt-1">请在工作流编辑器中添加 Excel 或 CSV 输入节点</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {inputNodes.map((node, index) => (
                    <InputFileUploader
                      key={node.id}
                      node={node}
                      index={index}
                      isDragging={draggingNodeId === node.id}
                      onDragStart={() => setDraggingNodeId(node.id)}
                      onDragEnd={() => setDraggingNodeId(null)}
                      onDrop={(e) => handleDrop(e, node.id)}
                      onFileSelect={(file) => handleFileSelect(node.id, file)}
                      onClear={() => handleClearFile(node.id)}
                      disabled={runState !== 'idle'}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Step 2: Execute */}
            {allFilesSelected && (
              <div>
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">
                  第二步：执行工作流
                </p>

                <AnimatePresence mode="wait">
                  {runState === 'idle' && (
                    <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200 text-xs text-zinc-500">
                        所有输入文件已就绪，共 {inputNodes.length} 个文件。
                        点击"执行"按钮运行工作流<span className="font-medium text-zinc-700"> "{workflow.name}"</span>。
                      </div>
                    </motion.div>
                  )}

                  {(runState === 'uploading' || runState === 'running') && (
                    <motion.div key="running" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="flex flex-col items-center py-6 gap-3"
                    >
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                        <Spinner className="w-8 h-8 text-emerald-500" weight="bold" />
                      </motion.div>
                      <p className="text-sm font-medium text-zinc-700">
                        {runState === 'uploading' ? '正在上传文件...' : '正在执行工作流...'}
                      </p>
                      <p className="text-xs text-zinc-400">请稍候，正在处理您的数据</p>
                    </motion.div>
                  )}

                  {runState === 'done' && executionResults.length > 0 && (
                    <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="flex flex-col min-h-0 -mx-2"
                    >
                      {/* 结果标签页（与设计页面一致） */}
                      <div className="flex items-center gap-1 px-2 mb-2 overflow-x-auto">
                        {executionResults.map((r, idx) => (
                          <button
                            key={r.nodeId}
                            onClick={() => setActiveResultTab(idx)}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                              activeResultTab === idx
                                ? 'bg-white text-zinc-900 shadow-sm border border-zinc-200'
                                : 'text-zinc-500 hover:text-zinc-700 hover:bg-white/60'
                            }`}
                          >
                            {r.isDiffResult ? (
                              <ArrowsLeftRight className="w-3.5 h-3.5" weight="fill" />
                            ) : (
                              <Lightning className="w-3.5 h-3.5" weight="fill" />
                            )}
                            {r.label}
                            <span className={`px-1.5 py-px rounded text-[10px] font-semibold ${
                              r.isDiffResult ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {r.rowCount.toLocaleString()}
                            </span>
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center justify-between gap-2 px-2 mb-2">
                        <span className="text-[10px] text-emerald-600 font-medium tabular-nums">
                          共 {executionResults[activeResultTab]?.rowCount.toLocaleString() ?? 0} 行
                        </span>
                        <button
                          onClick={() => {
                            const r = executionResults[activeResultTab];
                            const exportOverride = r?.isDiffResult
                              ? diffViewRef.current?.getExportData()
                              : undefined;
                            handleDownloadResult(activeResultTab, exportOverride);
                          }}
                          className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors border border-zinc-200 hover:border-emerald-200"
                        >
                          <DownloadSimple className="w-3 h-3" weight="bold" />
                          下载
                        </button>
                      </div>
                      {/* 结果内容区域 */}
                      <div className="flex-1 min-h-[200px] max-h-[320px] overflow-auto rounded-xl border border-zinc-200 bg-white">
                        {executionResults[activeResultTab] && (
                          executionResults[activeResultTab].isDiffResult ? (
                            <DiffResultView
                              ref={diffViewRef}
                              data={executionResults[activeResultTab].data}
                              columns={executionResults[activeResultTab].columns}
                              metadata={executionResults[activeResultTab].metadata}
                            />
                          ) : (
                            <DataTable
                              data={executionResults[activeResultTab].data}
                              columns={executionResults[activeResultTab].columns}
                            />
                          )
                        )}
                      </div>
                    </motion.div>
                  )}

                  {runState === 'error' && (
                    <motion.div key="error" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                      className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <WarningCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" weight="fill" />
                      <div>
                        <p className="text-sm font-medium text-red-700">执行失败</p>
                        <p className="text-xs text-red-500 mt-0.5">{errorMessage}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-zinc-50/50 border-t border-zinc-100 flex items-center justify-between gap-3 flex-shrink-0">
            <Button variant="secondary" onClick={handleClose}>
              {runState === 'done' ? '关闭' : '取消'}
            </Button>

            <div className="flex gap-2">
              {/* {runState === 'done' && executionResults.length > 0 && (
                <Button
                  variant="primary"
                  leftIcon={<DownloadSimple className="w-4 h-4" />}
                  onClick={() => {
                    const r = executionResults[activeResultTab];
                    const exportOverride = r?.isDiffResult ? diffViewRef.current?.getExportData() : undefined;
                    handleDownloadResult(activeResultTab, exportOverride);
                  }}
                >
                  下载结果
                </Button>
              )} */}
              {runState !== 'done' && (
                <Button
                  variant="accent"
                  leftIcon={
                    runState === 'uploading' || runState === 'running'
                      ? <Spinner className="w-4 h-4 animate-spin" weight="bold" />
                      : <Play className="w-4 h-4" weight="fill" />
                  }
                  onClick={handleExecute}
                  disabled={!allFilesSelected || runState === 'uploading' || runState === 'running'}
                >
                  {runState === 'uploading' ? '上传中...' : runState === 'running' ? '执行中...' : '执行'}
                </Button>
              )}
              {runState === 'error' && (
                <Button variant="secondary" onClick={resetState}>
                  重试
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Input File Uploader Component ─────────────────────────────────────────────

interface InputFileUploaderProps {
  node: InputNodeInfo;
  index: number;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent) => void;
  onFileSelect: (file: File) => void;
  onClear: () => void;
  disabled?: boolean;
}

function InputFileUploader({
  node,
  index,
  isDragging,
  onDragStart,
  onDragEnd,
  onDrop,
  onFileSelect,
  onClear,
  disabled,
}: InputFileUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isExcel = node.type === 'load_excel';
  const accept = isExcel ? '.xlsx,.xls' : '.csv';

  const handleClick = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    e.target.value = '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative"
    >
      {/* Node Label */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-xs font-medium text-zinc-600">{node.label}</span>
        <span className="text-[10px] text-zinc-400">({isExcel ? 'Excel' : 'CSV'})</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {!node.file ? (
        <div
          onDragOver={(e) => { e.preventDefault(); onDragStart(); }}
          onDragLeave={onDragEnd}
          onDrop={onDrop}
          onClick={handleClick}
          className={`
            border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all duration-200
            ${isDragging
              ? 'border-emerald-400 bg-emerald-50'
              : disabled
                ? 'border-zinc-200 bg-zinc-50 cursor-not-allowed'
                : 'border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50'
            }
          `}
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
              {isExcel ? (
                <FileXls className="w-5 h-5 text-zinc-400" weight="fill" />
              ) : (
                <Table className="w-5 h-5 text-zinc-400" weight="fill" />
              )}
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-zinc-700">
                {disabled ? '等待执行完成' : '点击或拖拽文件到此处'}
              </p>
              <p className="text-[10px] text-zinc-400 mt-0.5">
                支持 {accept} 格式
              </p>
            </div>
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl"
        >
          <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
            {isExcel ? (
              <FileXls className="w-5 h-5 text-emerald-600" weight="fill" />
            ) : (
              <Table className="w-5 h-5 text-emerald-600" weight="fill" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-900 truncate">{node.file.name}</p>
            <p className="text-[10px] text-emerald-600">
              {(node.file.size / 1024).toFixed(1)} KB · 已就绪
            </p>
          </div>
          {!disabled && (
            <button
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="p-1.5 text-emerald-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

// ── Workflow Card ─────────────────────────────────────────────────────────────
function WorkflowCard({
  workflow,
  onEdit,
  onDelete,
  onManageApps,
}: {
  workflow: SavedWorkflow;
  onEdit: (wf: SavedWorkflow) => void;
  onDelete: (wf: SavedWorkflow) => void;
  onManageApps: (wf: SavedWorkflow) => void;
}) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      className="group bg-white rounded-2xl border border-zinc-200/50 shadow-sm hover:shadow-lg hover:shadow-zinc-900/5 transition-all duration-300 overflow-hidden"
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <FlowArrow className="w-5 h-5 text-white" weight="fill" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900 group-hover:text-emerald-600 transition-colors">
                {workflow.name}
              </h3>
              <p className="text-xs text-zinc-500 mt-0.5">ID: {workflow.id.slice(-8)}</p>
            </div>
          </div>
          <StatusBadge status={workflow.status} />
        </div>

        {workflow.description && (
          <p className="text-sm text-zinc-600 line-clamp-2 mb-3">{workflow.description}</p>
        )}

        {/* File info */}
        {workflow.uploadedFile && (
          <div className="flex items-center gap-2 mb-3 px-2.5 py-1.5 bg-blue-50 rounded-lg border border-blue-100 w-fit">
            <FileXls className="w-3.5 h-3.5 text-blue-500" weight="fill" />
            <span className="text-xs text-blue-700 truncate max-w-[18ch]">
              {workflow.uploadedFile.name}
            </span>
          </div>
        )}

        <div className="flex items-center gap-4 text-xs text-zinc-500 mb-3">
          <div className="flex items-center gap-1">
            <DotsThree className="w-4 h-4" />
            <span>{workflow.nodeCount} 个节点</span>
          </div>
          <div className="flex items-center gap-1">
            <Play className="w-4 h-4" />
            <span>{workflow.executionCount} 次执行</span>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            <span>{new Date(workflow.createdAt).toLocaleDateString('zh-CN')}</span>
          </div>
          {workflow.lastExecutedAt && (
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>最近执行: {new Date(workflow.lastExecutedAt).toLocaleDateString('zh-CN')}</span>
            </div>
          )}
        </div>
      </div>

      <div className="px-5 py-3 bg-zinc-50/50 border-t border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => onManageApps(workflow)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-600 bg-white border border-zinc-200 rounded-lg hover:border-emerald-300 hover:text-emerald-600 transition-colors"
          >
            <AppWindow className="w-3.5 h-3.5" />
            应用
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => onEdit(workflow)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-600 bg-white border border-zinc-200 rounded-lg hover:border-emerald-300 hover:text-emerald-600 transition-colors"
          >
            <PencilSimple className="w-3.5 h-3.5" />
            编辑
          </motion.button>
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => onDelete(workflow)}
            className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export function WorkflowManagementPage() {
  const navigate = useNavigate();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToastActions();
  const { workflows, deleteWorkflow } = useWorkflowListStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [runningWorkflow, setRunningWorkflow] = useState<SavedWorkflow | undefined>();
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);

  const filteredWorkflows = workflows.filter((wf) => {
    const matchesSearch =
      wf.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      wf.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || wf.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleCreate = () => navigate('/home');

  const handleEdit = (wf: SavedWorkflow) => navigate(`/workflows/edit/${wf.id}`);

  const handleDelete = async (wf: SavedWorkflow) => {
    const confirmed = await confirm({
      title: '删除工作流',
      description: `确定要删除工作流 "${wf.name}" 吗？此操作不可撤销。`,
      confirmText: '确认删除',
      cancelText: '取消',
      type: 'danger',
    });
    if (confirmed) {
      deleteWorkflow(wf.id);
      toast.success('工作流已删除');
    }
  };

  const handleManageApps = (wf: SavedWorkflow) => {
    setRunningWorkflow(wf);
    setIsRunModalOpen(true);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-zinc-50 p-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">工作流管理</h1>
              <p className="mt-2 text-zinc-500">管理已保存的数据处理工作流，执行并下载结果</p>
            </div>
            <Button
              variant="primary"
              size="lg"
              leftIcon={<Plus className="w-5 h-5" />}
              onClick={handleCreate}
            >
              创建工作流
            </Button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 flex flex-wrap items-center gap-4"
        >
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder="搜索工作流名称或描述..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            {[
              { key: 'all',      label: '全部' },
              { key: 'active',   label: '已启用' },
              { key: 'draft',    label: '草稿' },
              { key: 'inactive', label: '已停用' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  statusFilter === key
                    ? 'bg-zinc-900 text-white'
                    : 'bg-white text-zinc-600 border border-zinc-200 hover:border-zinc-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Grid */}
        {filteredWorkflows.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-24 text-center"
          >
            <div className="w-20 h-20 rounded-3xl bg-zinc-100 flex items-center justify-center mb-6">
              <FlowArrow className="w-10 h-10 text-zinc-300" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">
              {searchQuery ? '未找到匹配的工作流' : '暂无工作流'}
            </h3>
            <p className="text-sm text-zinc-500 max-w-[40ch] mb-6">
              {searchQuery
                ? '尝试调整搜索关键词'
                : '点击"创建工作流"开始设计第一个数据处理流程'}
            </p>
            {!searchQuery && (
              <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={handleCreate}>
                创建工作流
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredWorkflows.map((wf) => (
                <WorkflowCard
                  key={wf.id}
                  workflow={wf}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onManageApps={handleManageApps}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Summary */}
        {workflows.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-12 pt-8 border-t border-zinc-200"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-zinc-500">
              <p>
                共 <span className="font-medium text-zinc-900">{workflows.length}</span> 个工作流
                {filteredWorkflows.length !== workflows.length && (
                  <span>，当前显示 {filteredWorkflows.length} 个</span>
                )}
              </p>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span>已启用: {workflows.filter((w) => w.status === 'active').length}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span>草稿: {workflows.filter((w) => w.status === 'draft').length}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-zinc-400" />
                  <span>已停用: {workflows.filter((w) => w.status === 'inactive').length}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Run Workflow Modal */}
      <RunWorkflowModal
        isOpen={isRunModalOpen}
        onClose={() => setIsRunModalOpen(false)}
        workflow={runningWorkflow}
      />

      {confirmDialog}
    </div>
  );
}
